import React, { useEffect, useState, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { get, post, del } from './api';
import { t, LanguageSwitcher } from '@omnifuse/shared/frontend/i18n';
import { SettingsPanel } from '@omnifuse/shared/frontend/SettingsPanel';
import { get as apiGet, post as apiPost, put as apiPut } from './api';

const S = {
  page: { maxWidth: 1100, margin: '0 auto', padding: 24 } as React.CSSProperties,
  card: { background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,.08)', marginBottom: 16 } as React.CSSProperties,
  btn: { padding: '6px 14px', borderRadius: 8, border: '1px solid #ccc', background: '#fff', cursor: 'pointer', fontSize: 13 } as React.CSSProperties,
  btnPrimary: { padding: '6px 14px', borderRadius: 8, border: 'none', background: '#1a73e8', color: '#fff', cursor: 'pointer', fontSize: 13 } as React.CSSProperties,
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 13 },
  th: { textAlign: 'left' as const, padding: '8px 10px', borderBottom: '2px solid #eee', color: '#555' },
  td: { padding: '8px 10px', borderBottom: '1px solid #f0f0f0' },
  tab: (a: boolean) => ({ padding: '8px 18px', cursor: 'pointer', borderBottom: a ? '2px solid #1a73e8' : '2px solid transparent', color: a ? '#1a73e8' : '#555', fontWeight: a ? 600 : 400, fontSize: 14 }) as React.CSSProperties,
  input: { padding: '6px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13 } as React.CSSProperties,
};

const LEVEL_BADGE: Record<string, { label: string; color: string }> = {
  pass: { label: t('✅ 通过'), color: '#188038' },
  warn: { label: t('⚠️ 警告'), color: '#e8710a' },
  block: { label: t('🚫 拦截'), color: '#d93025' },
};

// ═══ 即时检测 ═══
function CheckView() {
  const [text, setText] = useState('');
  const [category, setCategory] = useState('general');
  const [result, setResult] = useState<any>(null);
  const [busy, setBusy] = useState(false);

  const scan = async () => {
    if (!text) return alert('请粘贴内容');
    setBusy(true);
    try { const r: any = await post('/api/v1/scan/text', { text, category, content_type: 'ad' }); setResult(r.data); }
    catch (e: any) { alert(e.message); }
    setBusy(false);
  };

  return (
    <>
      <div style={S.card}>
        <h3 style={{ fontSize: 15, marginBottom: 12 }}>{t('上线前合规检测（PDP 描述 / 广告创意 / 博客）')}</h3>
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <select style={S.input} value={category} onChange={e => setCategory(e.target.value)}>
            <option value="general">{t('通用')}</option><option value="supplements">{t('保健品')}</option>
            <option value="medical">{t('医疗护理')}</option><option value="cosmetics">{t('美妆')}</option>
            <option value="financial">{t('金融')}</option>
          </select>
          <button style={S.btnPrimary} disabled={busy} onClick={scan}>{busy ? '扫描中...' : t('🛡️ 扫描内容')}</button>
        </div>
        <textarea style={{ ...S.input, width: '100%', height: 140 }} placeholder={t('粘贴待检测内容...')} value={text} onChange={e => setText(e.target.value)} />
      </div>
      {result && (
        <div style={{ ...S.card, borderLeft: `4px solid ${LEVEL_BADGE[result.risk_level].color}` }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: LEVEL_BADGE[result.risk_level].color, marginBottom: 10 }}>
            {LEVEL_BADGE[result.risk_level].label}{result.blocked && t(' — 建议修改后再发布')}
          </div>
          {result.findings.map((f: any, i: number) => (
            <div key={i} style={{ borderBottom: '1px solid #f5f5f5', padding: '8px 0', fontSize: 13 }}>
              <div>
                <span style={{ color: f.severity === 'block' ? '#d93025' : '#e8710a', fontWeight: 700 }}>
                  [{f.severity === 'block' ? '拦截' : t('警告')}]
                </span>{' '}
                <strong>"{f.term}"</strong>
                <span style={{ color: '#888', fontSize: 12 }}>（{f.source === 'rule' ? '词库命中' : t('AI 语义识别')}）</span>
              </div>
              {f.excerpt && <div style={{ color: '#666', fontSize: 12, margin: '2px 0' }}>{f.excerpt}</div>}
              {f.suggestion && <div style={{ color: '#188038', fontSize: 12 }}>💡 建议替代：{f.suggestion}</div>}
            </div>
          ))}
          {result.findings.length === 0 && <div style={{ color: '#188038' }}>{t('未发现违规内容')}</div>}
        </div>
      )}
    </>
  );
}

// ═══ 全店扫描 ═══
function ScansView() {
  const [rows, setRows] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  const load = () => get('/api/v1/scans').then((r: any) => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const scanAll = async () => {
    setBusy(true);
    try { const r: any = await post('/api/v1/scan/products', {}); alert(`扫描 ${r.data.scanned} 个商品：拦截 ${r.data.blocked}，警告 ${r.data.warned}，通过 ${r.data.passed}`); load(); }
    catch (e: any) { alert(e.message); }
    setBusy(false);
  };

  return (
    <div style={S.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        <h3 style={{ fontSize: 15 }}>{t('扫描记录')}</h3>
        <button style={S.btnPrimary} disabled={busy} onClick={scanAll}>{busy ? '扫描中（约 1-2 分钟）...' : t('🔍 全店 PDP 扫描')}</button>
      </div>
      <table style={S.table}>
        <thead><tr><th style={S.th}>{t('内容')}</th><th style={S.th}>{t('类型')}</th><th style={S.th}>{t('结果')}</th><th style={S.th}>{t('问题数')}</th><th style={S.th}>{t('时间')}</th></tr></thead>
        <tbody>
          {rows.map((r: any) => {
            const findings = JSON.parse(r.findings_json || '[]');
            const badge = LEVEL_BADGE[r.risk_level] || LEVEL_BADGE.pass;
            return (
              <React.Fragment key={r.id}>
                <tr style={{ cursor: 'pointer' }} onClick={() => setExpanded(expanded === r.id ? null : r.id)}>
                  <td style={S.td}>{r.resource_title || r.content_excerpt?.slice(0, 50)}</td>
                  <td style={S.td}>{r.content_type}</td>
                  <td style={{ ...S.td, color: badge.color, fontWeight: 600 }}>{badge.label}</td>
                  <td style={S.td}>{findings.length}</td>
                  <td style={S.td}>{(r.scanned_at || '').slice(0, 16)}</td>
                </tr>
                {expanded === r.id && findings.length > 0 && (
                  <tr><td colSpan={5} style={{ ...S.td, background: '#fafafa' }}>
                    {findings.map((f: any, i: number) => (
                      <div key={i} style={{ fontSize: 12, padding: '3px 0' }}>
                        <strong style={{ color: f.severity === 'block' ? '#d93025' : '#e8710a' }}>"{f.term}"</strong>
                        {f.suggestion && <span style={{ color: '#188038' }}> → {f.suggestion}</span>}
                      </div>
                    ))}
                  </td></tr>
                )}
              </React.Fragment>
            );
          })}
          {rows.length === 0 && <tr><td style={S.td} colSpan={5}>{t('暂无扫描记录')}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// ═══ 违禁词库 ═══
function TermsView() {
  const [rows, setRows] = useState<any[]>([]);
  const [form, setForm] = useState({ term: '', category: 'general', severity: 'warn', suggestion: '', platform: '*' });

  const load = () => get('/api/v1/terms').then((r: any) => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.term) return alert('请填写词条');
    try { await post('/api/v1/terms', form); setForm({ ...form, term: '', suggestion: '' }); load(); }
    catch (e: any) { alert(e.message); }
  };

  return (
    <div style={S.card}>
      <h3 style={{ fontSize: 15, marginBottom: 12 }}>违禁词库（内置 {rows.filter((r: any) => r.source === 'builtin').length} + 自定义 {rows.filter((r: any) => r.source === 'custom').length}）</h3>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <input style={{ ...S.input, width: 160 }} placeholder={t('违禁词')} value={form.term} onChange={e => setForm({ ...form, term: e.target.value })} />
        <select style={S.input} value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
          <option value="general">{t('通用')}</option><option value="supplements">{t('保健品')}</option>
          <option value="medical">{t('医疗')}</option><option value="cosmetics">{t('美妆')}</option><option value="financial">{t('金融')}</option>
        </select>
        <select style={S.input} value={form.severity} onChange={e => setForm({ ...form, severity: e.target.value })}>
          <option value="warn">{t('警告')}</option><option value="block">{t('拦截')}</option>
        </select>
        <input style={{ ...S.input, width: 200 }} placeholder={t('合规替代措辞')} value={form.suggestion} onChange={e => setForm({ ...form, suggestion: e.target.value })} />
        <button style={S.btnPrimary} onClick={add}>{t('添加')}</button>
      </div>
      <table style={S.table}>
        <thead><tr><th style={S.th}>{t('词条')}</th><th style={S.th}>{t('类目')}</th><th style={S.th}>{t('平台')}</th><th style={S.th}>{t('级别')}</th><th style={S.th}>{t('替代措辞')}</th><th style={S.th}>{t('来源')}</th><th style={S.th}></th></tr></thead>
        <tbody>
          {rows.map((r: any) => (
            <tr key={r.id}>
              <td style={{ ...S.td, fontWeight: 600 }}>{r.term}</td>
              <td style={S.td}>{r.category}</td>
              <td style={S.td}>{r.platform}</td>
              <td style={{ ...S.td, color: r.severity === 'block' ? '#d93025' : '#e8710a' }}>{r.severity}</td>
              <td style={{ ...S.td, fontSize: 12 }}>{r.suggestion}</td>
              <td style={S.td}>{r.source === 'builtin' ? '内置' : t('自定义')}</td>
              <td style={S.td}>{r.source === 'custom' && <button style={S.btn} onClick={async () => { await del(`/api/v1/terms/${r.id}`); load(); }}>{t('删除')}</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function App() {
  const [tab, setTab] = useState('check');
  return (
    <div style={S.page}>
      <div style={{ position: 'fixed', top: 12, right: 16, zIndex: 1000 }}><LanguageSwitcher /></div>
      <h1 style={{ fontSize: 22, marginBottom: 4 }}>ComplianceGuardian</h1>
      <p style={{ color: '#666', marginBottom: 16 }}>{t('合规违禁内容守卫 — 违禁词库 · 语义扫描 · 上线前拦截')}</p>
      <div style={{ display: 'flex', borderBottom: '1px solid #eee', marginBottom: 20 }}>
        <div style={S.tab(tab === 'check')} onClick={() => setTab('check')}>{t('即时检测')}</div>
        <div style={S.tab(tab === 'scans')} onClick={() => setTab('scans')}>{t('全店扫描')}</div>
        <div style={S.tab(tab === 'terms')} onClick={() => setTab('terms')}>{t('违禁词库')}</div>
        <div style={S.tab(tab === 'settings')} onClick={() => setTab('settings')}>{t('设置')}</div>
      </div>
      {tab === 'check' && <CheckView />}
      {tab === 'scans' && <ScansView />}
      {tab === 'terms' && <TermsView />}
      {tab === 'settings' && <SettingsPanel api={{ get: apiGet, post: apiPost, put: apiPut }} />}
    </div>
  );
}

const root = createRoot(document.getElementById('root')!);
root.render(<App />);
